import { Modal } from "@mui/material";
import { Close, FileDownload, FileUpload } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";

import { formatSlotTime, formatToDecimals } from "../../../helpers/dateFormat";
import { ErrorMessage, SuccessMessage } from "../../../commons/Toast/ErrorMessage";
import Styles from "./ScheduleModal.module.css";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const COL_WIDTHS = {
  time: "10%",
  volMw: "20%",
  volMwh: "15%",
};

const TABLE_HEADERS = [
  { label: "Start Time", width: COL_WIDTHS.time },
  { label: "End Time", width: COL_WIDTHS.time },
  { label: "Sch. Volume (MW)", width: COL_WIDTHS.volMw },
  { label: "Sch Volume (MWh)", width: COL_WIDTHS.volMwh },
  { label: "Del. Volume (MW)", width: COL_WIDTHS.volMw },
  { label: "Del. Volume (MWh)", width: COL_WIDTHS.volMwh },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const parseEndTime = (rawEnd) =>
  rawEnd?.trim() === "23:59:59" ? "24:00" : formatSlotTime(rawEnd?.trim());

const buildInitialBlocks = (blocks, type) =>
  blocks?.map((block) => {
    if (type === "deliver") {
      return {
        ...block,
        deliveredVolume: block?.scheduleVolume?.toFixed(2),
        deliveredEditVolume: block?.scheduleVolume?.toFixed(2),
      };
    }
    if (type === "edit") {
      return {
        ...block,
        scheduleEditVolume: block?.scheduleVolume?.toFixed(2),
      };
    }
    return block;
  });

const buildUpdatedBlocks = (viewUpdate, type) =>
  viewUpdate?.map(({ scheduleEditVolume, deliveredEditVolume, ...rest }) => ({
    ...rest,
    scheduleVolume: Number(
      type === "edit" ? scheduleEditVolume : rest.scheduleVolume,
    ),
    deliveredVolume: Number(
      type === "deliver" ? deliveredEditVolume : rest.deliveredVolume,
    ),
  }));

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const TableHeader = () => (
  <div className="display_flex_row tableHeading_type2">
    {TABLE_HEADERS.map(({ label, width }) => (
      <div key={label} style={{ width }}>
        {label}
      </div>
    ))}
  </div>
);

const EditableInput = ({ value, onChange, styles }) => (
  <div className={styles.edit_input_schedule}>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={styles.editInput}
    />
  </div>
);

const ViewDetails = ({ ele, index, type, onVolumeChange, uploadVersion }) => {
  const [scheduleMw, setScheduleMw] = useState((ele?.scheduleVolume || 0) * 4);
  const [deliveredMw, setDeliveredMw] = useState(
    type === "deliver" ? (ele?.scheduleVolume || 0) * 4 : 0,
  );

  useEffect(() => {
    if (uploadVersion > 0) {
      if (type === "edit") {
        setScheduleMw((Number(ele?.scheduleEditVolume) ?? 0) * 4);
      } else if (type === "deliver") {
        setDeliveredMw(
          (ele?.deliveredEditVolume != null
            ? Number(ele.deliveredEditVolume)
            : ele?.scheduleVolume ?? 0) * 4,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadVersion]);

  const [startRaw, endRaw] = ele?.timeBlock?.split("-") ?? [];

  const sanitizeValue = (val) => {
    const num = Math.max(0, Number(val) || 0);
    return Math.round(num * 1000) / 1000;
  };

  const NUMERIC_REGEX = /^\d*\.?\d{0,3}$/;

  const handleScheduleChange = (val) => {
    if (val !== "" && !NUMERIC_REGEX.test(val)) {
      ErrorMessage("Only numeric values are allowed.");
      return;
    }
    if (val !== "" && Number(val) < 0) {
      ErrorMessage("Value cannot be negative.");
      return;
    }
    const max = ele?.scheduleVolume * 4;
    if (Number(val) > max) {
      ErrorMessage(
        `Scheduled MW cannot exceed original schedule volume (${max.toFixed(2)} MW).`,
      );
      setScheduleMw(max.toFixed(2));
      onVolumeChange(max / 4, index);
      return;
    }
    setScheduleMw(val);
    onVolumeChange(Number(val) / 4, index);
  };

  const handleDeliveredChange = (val) => {
    if (val !== "" && !NUMERIC_REGEX.test(val)) {
      ErrorMessage("Only numeric values are allowed.");
      return;
    }
    if (val !== "" && Number(val) < 0) {
      ErrorMessage("Value cannot be negative.");
      return;
    }
    // max is the current edited schedule MW for this slot
    const max = Number(scheduleMw);
    if (Number(val) > max) {
      ErrorMessage(
        `Delivered MW cannot exceed scheduled volume (${max.toFixed(2)} MW).`,
      );
      setDeliveredMw(max.toFixed(2));
      onVolumeChange(max / 4, index);
      return;
    }
    setDeliveredMw(val);
    onVolumeChange(Number(val) / 4, index);
  };

  // Sanitize on blur to clean up trailing decimals like "1."
  const handleScheduleBlur = () => setScheduleMw(sanitizeValue(scheduleMw));
  const handleDeliveredBlur = () => setDeliveredMw(sanitizeValue(deliveredMw));

  return (
    <div className={`display_flex_row tableDetails ${Styles.tab_det_cont}`}>
      {/* Start Time */}
      <div style={{ width: COL_WIDTHS.time }}>{formatSlotTime(startRaw)}</div>

      {/* End Time */}
      <div style={{ width: COL_WIDTHS.time }}>{parseEndTime(endRaw)}</div>

      {/* Sch. Volume (MW) */}
      <div
        style={{ width: COL_WIDTHS.volMw }}
        className="display_flex_row_gap_8"
      >
        {type === "edit" ? (
          <EditableInput
            value={scheduleMw}
            onChange={handleScheduleChange}
            styles={Styles}
          />
        ) : (
          <div className={Styles.text_input_head}>
            {Number(scheduleMw).toFixed(2)}
          </div>
        )}
      </div>

      {/* Sch. Volume (MWh) */}
      <div
        style={{ width: COL_WIDTHS.volMwh }}
        className="display_flex_row_gap_8"
      >
        {formatToDecimals(ele?.scheduleEditVolume ?? ele?.scheduleVolume)}
      </div>

      {/* Del. Volume (MW) */}
      <div
        style={{ width: COL_WIDTHS.volMw }}
        className="display_flex_row_gap_8"
      >
        {type === "deliver" ? (
          <EditableInput
            value={deliveredMw}
            onChange={handleDeliveredChange}
            styles={Styles}
          />
        ) : (
          <div className={Styles.text_input_head}>
            {Number(deliveredMw).toFixed(2)}
          </div>
        )}
      </div>

      {/* Del. Volume (MWh) */}
      <div style={{ width: COL_WIDTHS.volMwh }}>
        {formatToDecimals(ele?.deliveredEditVolume ?? ele?.deliveredVolume)}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const ScheduleUpdateModal = ({
  type,
  open,
  handleClose,
  blocks,
  handleUpdateSubmit,
  parentData,
  useSecondaryAccent = false,
}) => {
  const { bilateralContractUniqueId, counterPartyCompanyName } = parentData;

  const [viewUpdate, setViewUpdate] = useState(blocks);
  const [loading, setLoading] = useState(false);
  const [uploadVersion, setUploadVersion] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setViewUpdate(buildInitialBlocks(blocks, type));
  }, [blocks, type]);

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Schedule Report");

    sheet.columns = [
      { width: 11 }, // A: Start Time
      { width: 11 }, // B: End Time
      { width: 15 }, // C: Sch. Volume (MW)
      { width: 15 }, // D: Sch. Volume (MWh)
      { width: 15 }, // E: Del. Volume (MW)
      { width: 15 }, // F: Del. Volume (MWh)
      { width: 14 }, // G: MWh Variance
    ];

    const fill = (cell, argb) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb } };
    };
    const border = (cell) => {
      const side = { style: "thin", color: { argb: "FFE0E0E0" } };
      cell.border = { top: side, bottom: side, left: side, right: side };
    };

    // ── Row 1: Title ──────────────────────────────────────────────────
    sheet.mergeCells("A1:G1");
    const titleCell = sheet.getCell("A1");
    titleCell.value =
      type === "deliver"
        ? "⚡  Delivery Report  ⚡"
        : "⚡  Schedule Report  ⚡";
    fill(titleCell, "FF0D1B2A");
    titleCell.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(1).height = 32;

    // ── Row 2: Subtitle ───────────────────────────────────────────────
    sheet.mergeCells("A2:G2");
    const subCell = sheet.getCell("A2");
    subCell.value = `15-Minute Interval Energy Tracking  |  Date: ${parentData?.fromDate ?? "___________"}`;
    fill(subCell, "FF1B3A6B");
    subCell.font = { size: 10, color: { argb: "FFFFFFFF" }, italic: true };
    subCell.alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(2).height = 20;

    // ── Row 3: Contract details ───────────────────────────────────────
    const INFO_BG = "FF1E3A5C";
    const setInfo = (mergeAddr, label, value) => {
      sheet.mergeCells(mergeAddr);
      const cell = sheet.getCell(mergeAddr.split(":")[0]);
      cell.value = `${label}: ${value ?? "—"}`;
      fill(cell, INFO_BG);
      cell.font = { size: 10, bold: false, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
      cell.protection = { locked: true };
    };
    setInfo("A3:C3", "Contract ID", bilateralContractUniqueId);
    setInfo("D3:G3", "Counter Party", counterPartyCompanyName);
    sheet.getRow(3).height = 20;

    // ── Row 4: Group headers ──────────────────────────────────────────
    const GROUP = {
      time: "FF1A5276",
      scheduled: "FF1E8449",
      delivered: "FF145A32",
      variance: "FF6C3483",
    };
    const setGroup = (addr, label, colorKey) => {
      const cell = sheet.getCell(addr);
      cell.value = label;
      fill(cell, GROUP[colorKey]);
      cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    };
    sheet.mergeCells("A4:B4");
    setGroup("A4", "Time Interval", "time");
    sheet.mergeCells("C4:D4");
    setGroup("C4", "Scheduled", "scheduled");
    sheet.mergeCells("E4:F4");
    setGroup("E4", "Delivered", "delivered");
    setGroup("G4", "Variance", "variance");
    sheet.getRow(4).height = 22;

    // ── Row 5: Sub-column headers ─────────────────────────────────────
    const editableCol = type === "edit" ? "C" : "E";
    [
      { col: "A", label: "Start Time", color: GROUP.time },
      { col: "B", label: "End Time", color: GROUP.time },
      {
        col: "C",
        label: type === "edit" ? "Sch. Volume (MW) ✏" : "Sch. Volume (MW)",
        color: GROUP.scheduled,
      },
      { col: "D", label: "Sch. Volume (MWh)", color: GROUP.scheduled },
      {
        col: "E",
        label: type === "deliver" ? "Del. Volume (MW) ✏" : "Del. Volume (MW)",
        color: GROUP.delivered,
      },
      { col: "F", label: "Del. Volume (MWh)", color: GROUP.delivered },
      { col: "G", label: "MWh Variance", color: GROUP.variance },
    ].forEach(({ col, label, color }) => {
      const cell = sheet.getCell(`${col}5`);
      cell.value = label;
      fill(cell, color);
      cell.font = { bold: true, size: 10, color: { argb: "FFFFFFFF" } };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.protection = { locked: true };
    });
    sheet.getRow(5).height = 30;

    // ── Rows 6+: Data ────────────────────────────────────────────────
    const TEXT = {
      time: "FF000000",
      scheduled: "FF1F618D",
      delivered: "FF1E8449",
      variance: "FF6C3483",
    };
    // Light amber background marks the one editable column
    const EDIT_BG = "FFFFFDE7";

    viewUpdate?.forEach((ele, i) => {
      const [startRaw, endRaw] = ele?.timeBlock?.split("-") ?? [];
      const schMwh = Number(ele.scheduleEditVolume ?? ele.scheduleVolume ?? 0);
      const delMwh = Number(
        ele.deliveredEditVolume ?? ele.deliveredVolume ?? 0,
      );
      const schMw = schMwh * 4;
      const delMw = delMwh * 4;
      const variance = Number((delMwh - schMwh).toFixed(4));
      const rowNum = i + 6;
      const bg = i % 2 === 0 ? "FFFFFFFF" : "FFF4F6F7";

      const set = (col, value, textKey, numFmt) => {
        const isEditable = col === editableCol;
        const cell = sheet.getCell(`${col}${rowNum}`);
        cell.value = value;
        fill(cell, isEditable ? EDIT_BG : bg);
        cell.font = { color: { argb: TEXT[textKey] }, size: 10 };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        if (numFmt) cell.numFmt = numFmt;
        border(cell);
        cell.protection = { locked: !isEditable };
      };

      set("A", formatSlotTime(startRaw), "time");
      set("B", parseEndTime(endRaw), "time");
      set("C", Number(schMw.toFixed(2)), "scheduled", "0.00");
      set("E", Number(delMw.toFixed(2)), "delivered", "0.00");

      // Data validation on the editable column
      if (type === "edit") {
        const maxMw = (ele.scheduleVolume || 0) * 4;
        sheet.getCell(`C${rowNum}`).dataValidation = {
          type: "decimal",
          operator: "between",
          formulae: [0, maxMw],
          showErrorMessage: true,
          errorStyle: "stop",
          errorTitle: "Invalid Input",
          error: `Must be 0 – ${maxMw.toFixed(2)} MW. Negative and non-numeric values are not allowed.`,
          promptTitle: "Scheduled Volume (MW)",
          prompt: `Enter a value between 0 and ${maxMw.toFixed(2)} MW`,
          showInputMessage: true,
        };
      } else if (type === "deliver") {
        sheet.getCell(`E${rowNum}`).dataValidation = {
          type: "custom",
          formulae: [`AND(E${rowNum}>=0,E${rowNum}<=C${rowNum})`],
          showErrorMessage: true,
          errorStyle: "stop",
          errorTitle: "Invalid Input",
          error:
            "Delivered MW must be ≥ 0 and ≤ the scheduled volume in column C. Negative and non-numeric values are not allowed.",
          promptTitle: "Delivered Volume (MW)",
          prompt: "Enter a value between 0 and the scheduled MW (col C)",
          showInputMessage: true,
        };
      }

      // D = Sch. Volume (MWh) = C / 4  [formula, locked]
      const dCell = sheet.getCell(`D${rowNum}`);
      dCell.value = {
        formula: `C${rowNum}/4`,
        result: Number(schMwh.toFixed(2)),
      };
      fill(dCell, bg);
      dCell.font = { color: { argb: TEXT.scheduled }, size: 10 };
      dCell.alignment = { horizontal: "center", vertical: "middle" };
      dCell.numFmt = "0.00";
      border(dCell);
      dCell.protection = { locked: true };

      // F = Del. Volume (MWh) = E / 4  [formula, locked]
      const fCell = sheet.getCell(`F${rowNum}`);
      fCell.value = {
        formula: `E${rowNum}/4`,
        result: Number(delMwh.toFixed(2)),
      };
      fill(fCell, bg);
      fCell.font = { color: { argb: TEXT.delivered }, size: 10 };
      fCell.alignment = { horizontal: "center", vertical: "middle" };
      fCell.numFmt = "0.00";
      border(fCell);
      fCell.protection = { locked: true };

      // G = MWh Variance = F - D  [formula, locked]
      const varCell = sheet.getCell(`G${rowNum}`);
      varCell.value = { formula: `F${rowNum}-D${rowNum}`, result: variance };
      fill(varCell, bg);
      varCell.font = { color: { argb: TEXT.variance }, size: 10 };
      varCell.alignment = { horizontal: "center", vertical: "middle" };
      varCell.numFmt = "0.00";
      border(varCell);
      varCell.protection = { locked: true };

      sheet.getRow(rowNum).height = 18;
    });

    // Protect sheet — only the editable column (✏) cells are unlocked
    await sheet.protect("", {
      selectLockedCells: true,
      selectUnlockedCells: true,
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${bilateralContractUniqueId}_schedule_vs_delivery_${type}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleUploadExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        // Validate contract ID from cell A3 matches the current contract
        const contractCell = sheet["A3"];
        const contractCellValue = contractCell
          ? String(contractCell.v ?? "")
          : "";
        const contractMatch = contractCellValue.match(/Contract ID:\s*(.+)/i);
        const uploadedContractId = contractMatch ? contractMatch[1].trim() : "";
        if (uploadedContractId !== bilateralContractUniqueId) {
          ErrorMessage(
            `Contract ID mismatch. Expected "${bilateralContractUniqueId}" but got "${uploadedContractId || "not found"}". Please upload the correct template.`,
          );
          return;
        }

        // Data rows start at Excel row 6 (rows 1-5 are headers/metadata).
        // Access cells directly by address — same layout as the download writer
        // which uses rowNum = i + 6. Avoids all sheet_to_json header-matching issues.
        // Editable column: C for "edit", E for "deliver".
        const editColLetter = type === "edit" ? "C" : "E";
        const stateField =
          type === "edit" ? "scheduleEditVolume" : "deliveredEditVolume";

        let hasError = false;
        const updated = (viewUpdate || []).map((ele, i) => {
          const cell = sheet[`${editColLetter}${i + 6}`];
          if (!cell || cell.v === undefined || cell.v === null || cell.v === "")
            return ele;
          const raw = cell.v;
          if (typeof raw === "string" && /[a-zA-Z]/.test(raw)) {
            ErrorMessage(`Row ${i + 1}: Alphabetic values are not allowed.`);
            hasError = true;
            return ele;
          }
          const mwVal = Number(raw);
          if (isNaN(mwVal)) {
            ErrorMessage(`Row ${i + 1}: Only numeric values are allowed.`);
            hasError = true;
            return ele;
          }
          if (mwVal < 0) {
            ErrorMessage(`Row ${i + 1}: Negative values are not allowed.`);
            hasError = true;
            return ele;
          }
          const maxMw =
            type === "deliver"
              ? (Number(ele.scheduleEditVolume ?? ele.scheduleVolume) || 0) * 4
              : (ele.scheduleVolume || 0) * 4;
          if (mwVal > maxMw) {
            ErrorMessage(
              `Row ${i + 1}: Value (${mwVal} MW) exceeds scheduled volume (${maxMw.toFixed(2)} MW).`,
            );
            hasError = true;
            return ele;
          }
          return { ...ele, [stateField]: mwVal / 4 };
        });

        if (!hasError) {
          setViewUpdate(updated);
          setUploadVersion((v) => v + 1);
          SuccessMessage("Excel uploaded successfully.");
        }
      } catch {
        ErrorMessage(
          "Failed to parse Excel file. Please use the downloaded template.",
        );
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleVolumeChange = (val, index) => {
    const field =
      type === "edit" ? "scheduleEditVolume" : "deliveredEditVolume";
    setViewUpdate((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: val };
      return updated;
    });
  };

  const handleUpdate = () => {
    setLoading(true);
    Promise.resolve(
      handleUpdateSubmit(buildUpdatedBlocks(viewUpdate, type)),
    ).finally(() => setLoading(false));
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="child-modal-title"
      aria-describedby="child-modal-description"
    >
      <div
        className={`popupModal ${Styles.scheduleEditModal} ${
          useSecondaryAccent ? "secondaryAccent" : ""
        }`}
      >
        {/* Header */}
        <div className={`display_flex_row modalHeading ${Styles.modalHeading}`}>
          <div className="headingText">
            {type === "edit" ? "Edit Schedule" : "Confirm Schedule"}
          </div>
          <div className={Styles.headerActions}>
            <button
              className={Styles.excelBtn}
              onClick={() =>
                handleDownloadExcel().catch(() =>
                  ErrorMessage("Download failed. Please try again."),
                )
              }
              title={`Download Excel — edit the "${type === "edit" ? "Sch. Volume (MW)" : "Del. Volume (MW)"}" column and re-upload`}
            >
              <FileDownload style={{ fontSize: 16 }} />
              Download
            </button>
            <button
              className={Styles.excelBtn}
              onClick={() => fileInputRef.current?.click()}
              title="Upload filled Excel template"
            >
              <FileUpload style={{ fontSize: 16 }} />
              Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleUploadExcel}
            />
            <Close
              style={{ fontSize: 24, color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>
        </div>

        {/* Table */}
        <div className={Styles.scheduleEditMainCont}>
          <TableHeader />
          <div className={`scroll_bar_y ${Styles.scrollMainSection}`}>
            {viewUpdate?.map((ele, index) => (
              <ViewDetails
                key={index}
                ele={ele}
                index={index}
                type={type}
                onVolumeChange={handleVolumeChange}
                uploadVersion={uploadVersion}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className={`display_flex_row_center ${Styles.buttonMainCont}`}>
          <button
            style={{ width: "200px", height: "40px", border: "0px" }}
            onClick={handleUpdate}
            className={`primary-button ${Styles.buttonList}`}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ScheduleUpdateModal;
